import Logger from '../lib/logger';
import { buildFilteringFacetValueMap, filteringFacetKey } from '../lib/filtering-facets';

const NAME = 'ProductSpecGrid';
const FACET_VALUE_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const SHOPIFY_FACET_KEY = {
  product_type: '__shopify_product_type__',
  vendor: '__shopify_vendor__',
  tag: '__shopify_tag__',
  category: '__shopify_category__',
};

const SHOPIFY_FACET_DEFAULT_LABEL = {
  product_type: 'Type',
  vendor: 'Vendor',
  tag: 'Tag',
  category: 'Category',
};

const METAFIELD_FACET_PREFIX = '__metafield__';

/**
 * @param {string} namespace
 * @param {string} key
 * @returns {string}
 */
function metafieldFacetKey(namespace, key) {
  return `${METAFIELD_FACET_PREFIX}${namespace}.${key}`;
}

/**
 * @param {string} key
 * @returns {string}
 */
function humanizeKey(key) {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * @param {unknown} v
 * @returns {string | null}
 */
function stringifySpecValue(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'boolean') return v ? String(v) : null;
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : null;
  if (typeof v === 'string') {
    const t = v.trim().replace(/\s+/g, ' ');
    return t === '' ? null : t;
  }
  try {
    return JSON.stringify(v);
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} specs
 * @returns {Record<string, string>}
 */
function normalizeSpecMap(specs) {
  const out = {};
  if (!specs || typeof specs !== 'object' || Array.isArray(specs)) return out;

  for (const [rawKey, val] of Object.entries(specs)) {
    const key = rawKey.trim();
    if (!key) continue;
    const s = stringifySpecValue(val);
    if (s == null) continue;
    out[key] = s;
  }
  return out;
}

/**
 * Coerces a raw metafield value into a string, an array of strings, or null.
 * Handles scalar, array, and simple reference-with-title shapes.
 *
 * @param {unknown} raw
 * @returns {string | string[] | null}
 */
function normalizeMetafieldValue(raw) {
  if (raw == null) return null;

  if (Array.isArray(raw)) {
    /** @type {string[]} */
    const out = [];
    for (const item of raw) {
      const single = normalizeMetafieldValue(item);
      if (typeof single === 'string') out.push(single);
      else if (Array.isArray(single)) out.push(...single);
    }
    return out.length ? out : null;
  }

  if (typeof raw === 'object') {
    if (typeof raw.title === 'string') return stringifySpecValue(raw.title);
    if (typeof raw.name === 'string') return stringifySpecValue(raw.name);
    if (typeof raw.value === 'string') return stringifySpecValue(raw.value);
    if (typeof raw.handle === 'string') return stringifySpecValue(raw.handle);
    return null;
  }

  return stringifySpecValue(raw);
}

/**
 * Builds the full per-facet value map for a product, combining Salsify specs,
 * Shopify fields (type/vendor/tags/category), and custom metafields. Values are
 * either a single canonical string or an array of canonical strings.
 *
 * @param {BootstrapProduct} product
 * @returns {Record<string, string | string[]>}
 */
function buildProductValueMap(product) {
  const map = {
    ...normalizeSpecMap(product?.specs),
    ...buildFilteringFacetValueMap(product?.filtering),
  };

  const productType = stringifySpecValue(product?.productType);
  if (productType) map[SHOPIFY_FACET_KEY.product_type] = productType;

  const vendor = stringifySpecValue(product?.vendor);
  if (vendor) map[SHOPIFY_FACET_KEY.vendor] = vendor;

  if (Array.isArray(product?.tags)) {
    const tags = product.tags
      .map((tag) => stringifySpecValue(tag))
      .filter((tag) => typeof tag === 'string' && tag.length > 0);
    if (tags.length) {
      map[SHOPIFY_FACET_KEY.tag] = tags;

      const categoryTags = tags
        .filter((tag) => tag.toLowerCase().startsWith('category:'))
        .map((tag) => tag.slice('category:'.length).trim())
        .filter((tag) => tag.length > 0);
      if (categoryTags.length) {
        map[SHOPIFY_FACET_KEY.category] = categoryTags;
      }
    }
  }

  if (product?.metafields && typeof product.metafields === 'object' && !Array.isArray(product.metafields)) {
    for (const [nsKey, raw] of Object.entries(product.metafields)) {
      const normalized = normalizeMetafieldValue(raw);
      if (normalized == null) continue;
      const [namespace, ...keyParts] = nsKey.split('.');
      const key = keyParts.join('.');
      if (!namespace || !key) continue;
      map[metafieldFacetKey(namespace, key)] = normalized;
    }
  }

  return map;
}

/**
 * @param {Record<string, string | string[]>} valueMap
 * @param {Map<string, Set<string>>} selection
 * @param {string | null} skipFacetKey
 */
function matchesSelection(valueMap, selection, skipFacetKey) {
  for (const [facetKey, valueSet] of selection) {
    if (skipFacetKey && facetKey === skipFacetKey) continue;
    if (!valueSet || valueSet.size === 0) continue;
    const pv = valueMap[facetKey];
    if (pv == null) return false;
    if (Array.isArray(pv)) {
      const anyMatch = pv.some((v) => valueSet.has(v));
      if (!anyMatch) return false;
    } else if (!valueSet.has(pv)) {
      return false;
    }
  }
  return true;
}

/**
 * @typedef {{
 *   id: number,
 *   handle?: string,
 *   title?: string,
 *   url?: string,
 *   productType?: string | null,
 *   vendor?: string | null,
 *   tags?: string[] | null,
 *   specs?: Record<string, unknown> | null,
 *   filtering?: Record<string, unknown> | null,
 *   metafields?: Record<string, unknown> | null,
 * }} BootstrapProduct
 *
 * @typedef {{ kind: 'spec', specKey: string, label: string | null, collapsedDefault: boolean }
 *   | { kind: 'filtering', filterKey: string, label: string | null, collapsedDefault: boolean }
 *   | { kind: 'shopify', field: 'product_type' | 'vendor' | 'tag' | 'category', label: string | null, collapsedDefault: boolean }
 *   | { kind: 'metafield', namespace: string, key: string, label: string | null, collapsedDefault: boolean }
 * } FacetBlockConfig
 */

class ProductSpecGrid extends HTMLElement {
  #abortController;
  /** @type {BootstrapProduct[]} */
  #products = [];
  /** @type {Map<number, HTMLElement>} */
  #cards = new Map();
  /** @type {Map<string, Set<string>>} */
  #selection = new Map();
  /** @type {ReturnType<ProductSpecGrid['buildFacetModel']>} */
  #facetModel = [];
  /** @type {HTMLElement | null} */
  #facetMount = null;
  /** @type {{ facetBlocks: FacetBlockConfig[] }} */
  #config = { facetBlocks: [] };
  /** @type {() => void} */
  #handleKeydown = () => {};
  /** @type {() => void} */
  #handleResize = () => {};

  connectedCallback() {
    Logger.info(`${NAME} connected`);

    const dataEl = this.querySelector('[data-product-spec-grid-json]');
    const cfgEl = this.querySelector('[data-product-spec-grid-config]');

    try {
      const raw = dataEl?.textContent?.trim() || '[]';
      this.#products = JSON.parse(raw);
      if (!Array.isArray(this.#products)) this.#products = [];
    } catch (e) {
      Logger.error(`${NAME} product JSON parse failed`, e);
      this.#products = [];
    }

    try {
      const cfg = cfgEl?.textContent?.trim() ? JSON.parse(cfgEl.textContent.trim()) : {};
      /** @type {FacetBlockConfig[]} */
      const facetBlocks = [];
      if (Array.isArray(cfg.facetBlocks)) {
        for (const row of cfg.facetBlocks) {
          if (!row || typeof row !== 'object') continue;
          const label = typeof row.label === 'string' && row.label.trim() !== '' ? row.label.trim() : null;
          // Default is "expanded" so existing instances (which predate the
          // setting) keep their previous behaviour with no editor action.
          const collapsedDefault = row.collapsedDefault === true;

          if (row.kind === 'shopify') {
            const field = typeof row.field === 'string' ? row.field.trim() : '';
            if (!Object.prototype.hasOwnProperty.call(SHOPIFY_FACET_KEY, field)) continue;
            facetBlocks.push({ kind: 'shopify', field, label, collapsedDefault });
            continue;
          }

          if (row.kind === 'metafield') {
            const namespace = typeof row.namespace === 'string' ? row.namespace.trim() : '';
            const key = typeof row.key === 'string' ? row.key.trim() : '';
            if (!namespace || !key) continue;
            facetBlocks.push({ kind: 'metafield', namespace, key, label, collapsedDefault });
            continue;
          }

          if (row.kind === 'filtering') {
            const filterKey = typeof row.filterKey === 'string' ? row.filterKey.trim() : '';
            if (!filterKey) continue;
            facetBlocks.push({ kind: 'filtering', filterKey, label, collapsedDefault });
            continue;
          }

          const specKey = typeof row.specKey === 'string' ? row.specKey.trim() : '';
          if (!specKey) continue;
          facetBlocks.push({ kind: 'spec', specKey, label, collapsedDefault });
        }
      }
      this.#config = { facetBlocks };
    } catch {
      this.#config = { facetBlocks: [] };
    }

    /** @type {NodeListOf<HTMLElement>} */
    const cells = this.querySelectorAll('[data-product-id]');
    cells.forEach((cell) => {
      const pid = Number.parseInt(cell.getAttribute('data-product-id'), 10);
      if (Number.isFinite(pid)) this.#cards.set(pid, cell);
    });

    this.#facetModel = ProductSpecGrid.buildFacetModel({
      products: this.#products.filter((row) => row && typeof row.id === 'number'),
      facetBlocks: this.#config.facetBlocks,
    });

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#facetMount = this.querySelector('[data-spec-filter-mount]');
    if (this.#facetMount instanceof HTMLElement && this.#facetModel.length > 0) {
      this.#renderFacetUi(signal);
    }

    this.#wireDrawer(signal);
    this.#wireReset(signal);

    const sidebar = this.querySelector('.product-spec-grid__sidebar');
    const mobileToggle = this.querySelector('[data-filter-toggle]');
    if (this.#facetModel.length === 0) {
      if (sidebar instanceof HTMLElement) sidebar.hidden = true;
      if (mobileToggle instanceof HTMLElement) mobileToggle.hidden = true;
      this.classList.add('product-spec-grid--no-filters');
    } else {
      if (sidebar instanceof HTMLElement) sidebar.hidden = false;
      if (mobileToggle instanceof HTMLElement) mobileToggle.hidden = false;
      this.classList.remove('product-spec-grid--no-filters');
    }

    this.#applyFiltersAndCounts();
    Logger.info(`${NAME} facets ${this.#facetModel.length}`);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#selection.clear();
    this.#unlockBodyScroll();
  }

  /**
   * @param {{ products: BootstrapProduct[], facetBlocks: FacetBlockConfig[] }} param0
   */
  static buildFacetModel({ products, facetBlocks }) {
    /** @type {Map<string, Set<string>>} */
    const agg = new Map();

    for (const p of products) {
      const valueMap = buildProductValueMap(p);
      for (const [key, value] of Object.entries(valueMap)) {
        if (!agg.has(key)) agg.set(key, new Set());
        const set = agg.get(key);
        if (Array.isArray(value)) {
          value.forEach((v) => set.add(v));
        } else {
          set.add(value);
        }
      }
    }

    /** @type {{ facetKey: string, label: string, collapsedDefault: boolean }[]} */
    const facetMeta = [];
    /** @type {Set<string>} */
    const seenFacets = new Set();

    for (const block of facetBlocks) {
      let facetKey;
      let defaultLabel;

      if (block.kind === 'shopify') {
        facetKey = SHOPIFY_FACET_KEY[block.field];
        defaultLabel = SHOPIFY_FACET_DEFAULT_LABEL[block.field] || block.field;
      } else if (block.kind === 'metafield') {
        facetKey = metafieldFacetKey(block.namespace, block.key);
        defaultLabel = humanizeKey(block.key);
      } else if (block.kind === 'filtering') {
        facetKey = filteringFacetKey(block.filterKey);
        defaultLabel = block.filterKey;
      } else {
        facetKey = block.specKey;
        defaultLabel = block.specKey;
      }

      if (!facetKey || seenFacets.has(facetKey)) continue;
      if (!agg.has(facetKey)) continue;
      seenFacets.add(facetKey);
      const label = block.label && block.label.trim() !== '' ? block.label.trim() : defaultLabel;
      facetMeta.push({ facetKey, label, collapsedDefault: block.collapsedDefault === true });
    }

    return facetMeta.map(({ facetKey, label, collapsedDefault }) => ({
      facetKey,
      label,
      collapsedDefault,
      values: [...(agg.get(facetKey) || [])]
        .sort((a, b) => FACET_VALUE_COLLATOR.compare(a, b))
        .map((canonical) => ({ canonical, label: canonical })),
    }));
  }

  /**
   * @param {AbortSignal} signal
   */
  #renderFacetUi(signal) {
    const mount = this.#facetMount;
    const model = this.#facetModel;
    const selection = this.#selection;
    if (!mount || !model.length) return;

    mount.textContent = '';
    const nsPrefix = `${this.dataset.sectionId || 'psg'}-facet-`;

    model.forEach((facet, fkIndex) => {
      const details = document.createElement('details');
      details.className = 'product-spec-grid__facet';
      details.dataset.facetGroup = facet.facetKey;
      // Honour the "Collapsed by default" setting from the matching filter
      // block. When unset (default), the facet stays open as before.
      details.open = !facet.collapsedDefault;

      const summary = document.createElement('summary');
      summary.className = 'product-spec-grid__facet-summary';
      const legend = document.createElement('span');
      legend.className = 'product-spec-grid__facet-legend';
      legend.textContent = facet.label;
      summary.append(legend);

      const body = document.createElement('div');
      body.className = 'product-spec-grid__facet-body';

      details.append(summary, body);

      facet.values.forEach(({ canonical }, vIndex) => {
        const id = `${nsPrefix}-${fkIndex}-${vIndex}`;
        const row = document.createElement('label');
        row.className = 'product-spec-grid__facet-label';
        row.htmlFor = id;

        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.id = id;
        cb.value = canonical;
        cb.dataset.facetKey = facet.facetKey;
        cb.dataset.facetValue = canonical;

        const text = document.createElement('span');
        text.className = 'product-spec-grid__facet-value';
        text.textContent = canonical;

        const countSpan = document.createElement('span');
        countSpan.className = 'product-spec-grid__facet-count';
        countSpan.dataset.facetCount = '';
        countSpan.textContent = '0';

        row.append(cb, text, countSpan);
        body.append(row);

        cb.addEventListener(
          'change',
          () => {
            let set = selection.get(facet.facetKey);
            if (!set) {
              set = new Set();
              selection.set(facet.facetKey, set);
            }
            if (cb.checked) set.add(canonical);
            else set.delete(canonical);

            if (set.size === 0) selection.delete(facet.facetKey);
            else selection.set(facet.facetKey, set);

            this.#applyFiltersAndCounts();
          },
          { signal },
        );
      });

      mount.append(details);
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #wireDrawer(signal) {
    const toggle = this.querySelector('[data-filter-toggle]');
    const backdrop = this.querySelector('[data-filter-backdrop]');
    const closers = this.querySelectorAll('[data-filter-close]');
    const panel = this.querySelector('[data-filter-root]');

    if (!toggle || !panel) return;

    toggle.addEventListener(
      'click',
      () => {
        const isOpen = this.classList.contains('product-spec-grid--drawer-open');
        if (isOpen) this.#closeDrawer();
        else this.#openDrawer();
      },
      { signal },
    );

    closers.forEach((el) => {
      el.addEventListener('click', () => this.#closeDrawer(), { signal });
    });

    backdrop?.addEventListener('click', () => this.#closeDrawer(), { signal });

    this.#handleKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('product-spec-grid--drawer-open')) {
        this.#closeDrawer();
      }
    };
    document.addEventListener('keydown', this.#handleKeydown, { signal });

    this.#handleResize = () => {
      if (window.innerWidth >= 1024 && this.classList.contains('product-spec-grid--drawer-open')) {
        this.#closeDrawer();
      }
    };
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  #openDrawer() {
    this.classList.add('product-spec-grid--drawer-open');
    const toggle = this.querySelector('[data-filter-toggle]');
    const backdrop = this.querySelector('[data-filter-backdrop]');
    const panel = this.querySelector('[data-filter-root]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    if (backdrop instanceof HTMLElement) backdrop.hidden = false;
    if (panel instanceof HTMLElement) panel.setAttribute('aria-modal', 'true');
    this.#lockBodyScroll();
  }

  #closeDrawer() {
    this.classList.remove('product-spec-grid--drawer-open');
    const toggle = this.querySelector('[data-filter-toggle]');
    const backdrop = this.querySelector('[data-filter-backdrop]');
    const panel = this.querySelector('[data-filter-root]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (backdrop instanceof HTMLElement) backdrop.hidden = true;
    if (panel instanceof HTMLElement) panel.setAttribute('aria-modal', 'false');
    this.#unlockBodyScroll();
  }

  #lockBodyScroll() {
    if (window.innerWidth >= 1024) return;
    document.documentElement.classList.add('product-spec-grid-drawer-lock');
    document.body.classList.add('product-spec-grid-drawer-lock');
  }

  #unlockBodyScroll() {
    document.documentElement.classList.remove('product-spec-grid-drawer-lock');
    document.body.classList.remove('product-spec-grid-drawer-lock');
  }

  /**
   * @param {AbortSignal} signal
   */
  #wireReset(signal) {
    // Multiple reset buttons can coexist (desktop top bar + mobile drawer
    // footer). Wire them all so any one of them clears the selection.
    const resetButtons = this.querySelectorAll('[data-filter-reset]');
    resetButtons.forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          this.#selection.clear();
          this.#facetMount?.querySelectorAll('input[type="checkbox"]').forEach((c) => {
            /** @type {HTMLInputElement} */ (c).checked = false;
          });
          this.#applyFiltersAndCounts();
        },
        { signal },
      );
    });
  }

  #applyFiltersAndCounts() {
    const sidebar = this.querySelector('.product-spec-grid__sidebar');
    const sidebarScrollTop = sidebar instanceof HTMLElement ? sidebar.scrollTop : null;
    const resultsRoot = this.querySelector('[data-filter-results]');
    const activeSummary = this.querySelector('[data-filter-active-summary]');
    const mobileCount = this.querySelector('[data-filter-mobile-count]');
    const applyLabel = this.querySelector('[data-filter-apply-label]');
    const emptyEl = this.querySelector('[data-product-filters-empty]');

    /** @type {Record<number, Record<string, string | string[]>>} */
    const valueById = {};
    this.#products.forEach((row) => {
      if (typeof row.id === 'number') {
        valueById[row.id] = buildProductValueMap(row);
      }
    });

    let visible = 0;
    for (const row of this.#products) {
      const vm = valueById[row.id] || {};
      const show = matchesSelection(vm, this.#selection, null);
      const tile = this.#cards.get(row.id);
      if (tile) {
        tile.hidden = !show;
        tile.style.display = show ? '' : 'none';
      }
      if (show) visible += 1;
    }

    if (resultsRoot instanceof HTMLElement) {
      const countEl = resultsRoot.querySelector('.product-spec-grid__results-count');
      const labelEl = resultsRoot.querySelector('.product-spec-grid__results-label');
      if (countEl instanceof HTMLElement) countEl.textContent = String(visible);
      if (labelEl instanceof HTMLElement) labelEl.textContent = visible === 1 ? 'result' : 'results';
    }

    if (applyLabel instanceof HTMLElement) {
      applyLabel.textContent = `Show ${visible} ${visible === 1 ? 'result' : 'results'}`;
    }

    let activeFilterCount = 0;
    this.#selection.forEach((vs) => {
      activeFilterCount += vs.size;
    });

    if (activeSummary instanceof HTMLElement) {
      if (activeFilterCount === 0) {
        activeSummary.textContent = '';
        activeSummary.hidden = true;
      } else {
        activeSummary.hidden = false;
        const filterWord = activeFilterCount === 1 ? 'filter' : 'filters';
        activeSummary.textContent = `${activeFilterCount} ${filterWord} active`;
      }
    }

    if (mobileCount instanceof HTMLElement) {
      if (activeFilterCount === 0) {
        mobileCount.textContent = '';
        mobileCount.hidden = true;
      } else {
        mobileCount.hidden = false;
        mobileCount.textContent = String(activeFilterCount);
      }
    }

    const hasSelection = activeFilterCount > 0;
    /** @type {NodeListOf<HTMLButtonElement>} */
    const resetButtons = this.querySelectorAll('[data-filter-reset]');
    resetButtons.forEach((btn) => {
      btn.disabled = !hasSelection;
      btn.setAttribute('aria-disabled', hasSelection ? 'false' : 'true');
      // Persistent reset buttons (e.g. the mobile drawer footer) stay visible
      // and just toggle disabled state to avoid layout shift in a sticky bar.
      // Non-persistent ones collapse out of the layout when there's nothing
      // to clear.
      if (!btn.hasAttribute('data-filter-reset-persistent')) {
        btn.hidden = !hasSelection;
      }
    });

    if (emptyEl instanceof HTMLElement) emptyEl.hidden = visible > 0;

    const mount = this.#facetMount;

    this.#facetModel.forEach((facet) => {
      const skipKey = facet.facetKey;
      facet.values.forEach(({ canonical }) => {
        if (!mount) return;

        /** @type {HTMLInputElement | null} */
        const checkbox = [...mount.querySelectorAll('input[type="checkbox"][data-facet-key]')].find((el) => {
          const c = /** @type {HTMLInputElement} */ (el);
          return c.dataset.facetKey === facet.facetKey && c.dataset.facetValue === canonical;
        }) || null;

        const row = checkbox?.closest('.product-spec-grid__facet-label');
        const countSpan = row?.querySelector('[data-facet-count]');

        if (!checkbox || !countSpan) return;

        let count = 0;
        this.#products.forEach((p) => {
          const vm = valueById[p.id] || {};
          if (!matchesSelection(vm, this.#selection, skipKey)) return;
          const pv = vm[facet.facetKey];
          if (pv == null) return;
          if (Array.isArray(pv)) {
            if (pv.includes(canonical)) count += 1;
          } else if (pv === canonical) {
            count += 1;
          }
        });

        countSpan.textContent = String(count);
        checkbox.disabled = count === 0 && !checkbox.checked;
        const wrapper = checkbox.closest('.product-spec-grid__facet-label');
        if (wrapper instanceof HTMLElement) {
          wrapper.dataset.disabled = checkbox.disabled ? 'true' : 'false';
        }
      });
    });

    // Updating product visibility and facet availability causes a large page
    // reflow. Keep the independently scrollable desktop filter panel anchored
    // where the customer left it instead of allowing scroll anchoring to move
    // the panel to its new bottom edge.
    if (sidebar instanceof HTMLElement && sidebarScrollTop != null) {
      sidebar.scrollTop = sidebarScrollTop;
      requestAnimationFrame(() => {
        if (sidebar.isConnected) sidebar.scrollTop = sidebarScrollTop;
      });
    }
  }
}

customElements.define('product-spec-grid', ProductSpecGrid);
